/**
 * AI Service - Powered by Anthropic Claude
 *
 * This service provides AI-powered features for PCNGuard Fleet using Claude:
 * - OCR and data extraction from ticket images/PDFs
 * - Contextual appeal letter generation
 * - Intelligent fleet chat assistant
 * - Hire agreement compliance auditing
 */

import { Ticket } from '../types';
import { inferPrivateStageFromText, normalizeStage } from '../utils/stage';
import { inferEnforcementType } from '../utils/enforcement';
import { fileToBase64, getMediaType, withRetry } from './aiService';
import { supabase, isMockMode } from './supabaseClient';

export interface ComplianceResult {
  score: number;
  status: 'Compliant' | 'At Risk' | 'Non-Compliant';
  summary: string;
  missing_clauses: string[];
  recommendations: string[];
  detected_fields: {
    vrm: boolean;
    dates: boolean;
    signature: boolean;
    liability_statement: boolean; // Renamed logic to check embedded clause
  };
  extracted_data?: {
    vrm?: string;
    hirer_name?: string;
    liability_clause_text?: string;
    hire_start?: string;
    hire_end?: string;
  };
  validation_warnings?: string[]; // Warnings for mismatches with expected data
}

type ClaudeFunctionResponse<T> =
  | { ok: true; data: T; meta?: Record<string, any> }
  | { ok: false; error: string; code?: string; meta?: Record<string, any>; [key: string]: any };

const getAiOrchestratorFunctionName = (): string => {
  const configured = ((import.meta as any).env?.VITE_AI_ORCHESTRATOR_FUNCTION_NAME as string | undefined)?.trim();
  return configured || 'ai-orchestrator';
};

const sha256Hex = async (file: File): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

const invokeEdgeFunction = async <T>(
  functionName: string,
  action: string,
  body: Record<string, any>
): Promise<{ data: T; meta?: Record<string, any> }> => {
  // Get the current session to ensure we have auth token
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('No active session. Please log in again.');
  }

  // Use direct fetch instead of supabase.functions.invoke to avoid CORS issues
  const functionUrl = `${(import.meta as any).env?.VITE_SUPABASE_URL}/functions/v1/${functionName}`;

  console.log('🔗 Calling edge function:', functionUrl);

  const response = await fetch(functionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      'apikey': (import.meta as any).env?.VITE_SUPABASE_ANON_KEY
    },
    body: JSON.stringify({ action, ...body })
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Edge function HTTP error:', response.status, errorText);
    let parsedError: any = null;
    try {
      parsedError = JSON.parse(errorText);
    } catch {
      parsedError = null;
    }
    if (response.status === 404) {
      throw new Error(
        `Edge function "${functionName}" was not found (404). ` +
        `Check Supabase Dashboard → Edge Functions name, or set VITE_AI_ORCHESTRATOR_FUNCTION_NAME to the deployed function name.`
      );
    }
    const error = new Error(parsedError?.error || `Edge function failed: ${response.status} ${errorText}`);
    (error as any).code = parsedError?.code;
    (error as any).details = parsedError;
    throw error;
  }

  const payload = await response.json() as ClaudeFunctionResponse<T>;
  if (!payload) throw new Error('AI function returned no data');
  if (!payload.ok) {
    const error = new Error(payload.error || 'AI function failed');
    (error as any).code = payload.code;
    (error as any).details = payload;
    throw error;
  }
  return { data: payload.data, meta: payload.meta };
};

const invokeAiOrchestrator = async <T>(action: string, body: Record<string, any>): Promise<{ data: T; meta?: Record<string, any> }> => {
  return invokeEdgeFunction<T>(getAiOrchestratorFunctionName(), action, body);
};

export const ClaudeService = {
  // 1. Data Extraction with Claude Vision API
  async extractTicketData(file: File) {
    console.log("🤖 Extracting ticket data using Claude Vision AI:", file.name);
    if (isMockMode) {
      console.log("⚠️  Running in MOCK MODE - using mock extraction");
      return generateMockExtraction(file);
    }

    try {
      // Convert file to base64
      const base64Data = await fileToBase64(file);
      const mediaType = getMediaType(file);

      const fileHash = await sha256Hex(file);

      console.log('📤 Sending to AI orchestration edge function...', {
        fileSize: file.size,
        mediaType,
        base64Length: base64Data.length,
        fileHash
      });

      const orchestrationResult = await withRetry(() =>
        invokeAiOrchestrator<any>('extract_ticket_data', {
          mediaType,
          base64Data,
          fileName: file.name,
          fileSize: file.size,
          fileHash
        })
      );
      const extractedData = orchestrationResult.data;

      console.log('✅ Extraction successful:', {
        extractedData,
        aiMeta: orchestrationResult.meta
      });

      const lowConfidenceFields: string[] = Array.isArray(extractedData?.low_confidence_fields)
        ? extractedData.low_confidence_fields
        : [];

      // Context-aware stage inference:
      // Fleet Mode defaults to NtO/postal PCN, but we MUST not misinterpret later-stage documents (e.g. Charge Certificate).
      // If stage is missing or flagged low-confidence, run document classification and prefer its stage.
      let stage: string | null = extractedData?.stage ? String(extractedData.stage) : null;
      const stageIsLowConfidence = lowConfidenceFields.map(String).includes('stage');

      if (!stage || stageIsLowConfidence) {
        try {
          const classification = await ClaudeService.classifyDocument(file);
          if (classification?.stage && classification.stage !== 'Unknown') {
            stage = classification.stage;
          }
        } catch {
          // Ignore classification failures; fall back below.
        }
      }

      if (!stage) {
        const name = file.name.toLowerCase();
        if (name.includes('charge') || name.includes('certificate')) stage = 'Charge Certificate';
        else if (name.includes('order for recovery') || name.includes('ofr') || name.includes('te3')) stage = 'Order for Recovery';
        else if (name.includes('nto') || name.includes('notice to owner')) stage = 'Notice to Owner';
        else if (name.includes('pcn') || name.includes('penalty')) stage = 'PCN Issued';
      }

      const enforcementType = inferEnforcementType({
        issuing_authority: extractedData?.issuing_authority,
        document_type: extractedData?.document_type,
        document_intent: extractedData?.document_intent,
        key_phrases: extractedData?.key_phrases,
        stage
      });

      if (enforcementType === 'private') {
        const stageHints = [
          stage,
          extractedData?.document_intent,
          ...(extractedData?.key_phrases || []),
          file.name
        ]
          .filter(Boolean)
          .join(' ');
        const privateStage = inferPrivateStageFromText(stageHints);
        if (privateStage) stage = privateStage;
      }

      const normalizedStage = normalizeStage(stage, enforcementType);
      const defaultStage = enforcementType === 'private' ? 'Parking Charge Notice' : 'Notice to Owner';

      return {
        pcn_number: extractedData.pcn_number || 'UNKNOWN',
        vrn: extractedData.vrn || 'UNKNOWN',
        issue_date: extractedData.issue_date || new Date().toISOString(),
        contravention_date: extractedData.contravention_date || new Date().toISOString(),
        amount: extractedData.amount || 0,
        discount_amount: extractedData.discount_amount || null,
        location: extractedData.location || 'Unknown Location',
        issuing_authority: extractedData.issuing_authority || 'Unknown Authority',
        stage: normalizedStage || defaultStage,
        contravention_type: extractedData.contravention_type || 'Unknown',
        low_confidence_fields: lowConfidenceFields,
        ai_job_id: orchestrationResult.meta?.ai_job_id,
        ai_model: orchestrationResult.meta?.model,
        ai_file_hash: orchestrationResult.meta?.file_hash,
        ai_cache_hit: Boolean(orchestrationResult.meta?.cache_hit),
        ai_input_tokens: orchestrationResult.meta?.input_tokens,
        ai_output_tokens: orchestrationResult.meta?.output_tokens,
        ai_cost_usd: orchestrationResult.meta?.cost_usd
      };

    } catch (error: any) {
      console.error('❌ AI extraction failed:', error);
      console.error('Error details:', {
        message: error?.message,
        name: error?.name,
        stack: error?.stack,
        fullError: error
      });

      // Check if it's an auth error
      if (error?.message?.includes('session') || error?.message?.includes('Unauthorized')) {
        console.error('🔒 Authentication error - user may need to log in again');
        throw new Error('Authentication failed. Please log out and log in again.');
      }

      throw new Error(error?.message || 'AI extraction failed. Please re-upload or enter the PCN manually.');
    }
  },

  async attachTicketMetadata(params: {
    ticketId: string;
    aiJobId: string;
    fileHash?: string | null;
    extractedData?: Record<string, any>;
    lowConfidenceFields?: string[];
  }) {
    if (isMockMode) return { ok: true };

    const result = await invokeAiOrchestrator<{ ok?: boolean }>('attach_ticket_metadata', {
      ticketId: params.ticketId,
      aiJobId: params.aiJobId,
      fileHash: params.fileHash || undefined,
      extractedData: params.extractedData || {},
      lowConfidenceFields: params.lowConfidenceFields || []
    });
    return result.data;
  },

  // 2. AI Appeal Letter Generation with Claude (Enhanced with Legal Context)
  async generateAppealLetter(
    ticket: Ticket,
    reason: string,
    appealStrategy?: any,
    authorityProfile?: any
  ): Promise<string> {
    console.log("🤖 Generating enhanced appeal letter with Claude AI for:", ticket.pcn_number);
    if (isMockMode) return generateTemplateAppeal(ticket, reason);

    try {
      const letter = await withRetry(() =>
        invokeAiOrchestrator<string>('generate_appeal_letter', {
          ticket,
          reason,
          appealStrategy,
          authorityProfile
        })
      );
      console.log('✅ Enhanced appeal letter generated successfully');
      return letter.data;

    } catch (error: any) {
      console.error('❌ AI appeal generation failed:', error);
      console.warn('⚠️  Falling back to template appeal');

      // Graceful fallback to templates
      return generateTemplateAppeal(ticket, reason);
    }
  },

  // 3. FleetMind Chat with RAG (Retrieval-Augmented Generation)
  async chatWithFleet(message: string): Promise<string> {
    console.log("🤖 FleetMind processing query:", message);
    if (isMockMode) return generateKeywordResponse(message);

    try {
      // Fetch relevant context from database (RAG approach)
      // In a full implementation, this would query the database for relevant tickets, drivers, etc.
      // For now, we'll simulate with placeholder context
      const contextData = await fetchFleetContext();

      const responseText = await withRetry(() =>
        invokeAiOrchestrator<string>('chat', { message, context: contextData })
      );
      console.log('✅ FleetMind response generated');
      return responseText.data;

    } catch (error: any) {
      console.error('❌ FleetMind AI failed:', error);
      console.warn('⚠️  Falling back to keyword matching');

      return generateKeywordResponse(message);
    }
  },

  // 4. Hire Agreement Compliance Audit with Claude Vision (ENHANCED)
  async analyzeHireAgreement(
    file: File,
    options?: {
      expectedHirerName?: string;
      expectedVRM?: string;
    }
  ): Promise<ComplianceResult> {
    console.log("🤖 Analyzing hire agreement with Claude Vision:", file.name);
    if (isMockMode) return generateMockComplianceResult(file, options);

    try {
      // Convert file to base64
      const base64Data = await fileToBase64(file);
      const mediaType = getMediaType(file);
      const result = await withRetry(() =>
        invokeAiOrchestrator<any>('analyze_hire_agreement', {
          mediaType,
          base64Data,
          fileName: file.name,
          fileSize: file.size,
          expected_hirer_name: options?.expectedHirerName,
          expected_vrm: options?.expectedVRM
        })
      );
      const analysis = result.data || {};

      console.log('✅ Compliance analysis complete. Score:', analysis.score);

      // Build validation warnings if expected values don't match
      const validation_warnings: string[] = [];
      if (options?.expectedHirerName && analysis.extracted_data?.hirer_name) {
        const normalizedExpected = options.expectedHirerName.toLowerCase().trim();
        const normalizedExtracted = analysis.extracted_data.hirer_name.toLowerCase().trim();
        if (!normalizedExtracted.includes(normalizedExpected) && !normalizedExpected.includes(normalizedExtracted)) {
          validation_warnings.push(`Hirer name mismatch: Expected "${options.expectedHirerName}" but found "${analysis.extracted_data.hirer_name}"`);
        }
      }
      if (options?.expectedVRM && analysis.extracted_data?.vrm) {
        const normalizedExpected = options.expectedVRM.replace(/\s/g, '').toUpperCase();
        const normalizedExtracted = analysis.extracted_data.vrm.replace(/\s/g, '').toUpperCase();
        if (normalizedExpected !== normalizedExtracted) {
          validation_warnings.push(`VRM mismatch: Expected "${options.expectedVRM}" but found "${analysis.extracted_data.vrm}"`);
        }
      }

      return {
        score: analysis.score,
        status: analysis.status,
        summary: analysis.summary,
        missing_clauses: analysis.missing_clauses || [],
        recommendations: analysis.recommendations || [],
        detected_fields: analysis.detected_fields,
        extracted_data: analysis.extracted_data || {},
        validation_warnings: validation_warnings.length > 0 ? validation_warnings : undefined
      };

    } catch (error: any) {
      console.error('❌ AI compliance audit failed:', error);
      console.warn('⚠️  Falling back to mock compliance result');

      return generateMockComplianceResult(file, options);
    }
  },

  // 5. Extract Hire Agreement Data with Claude Vision
  async extractHireAgreementData(file: File) {
    console.log("🤖 Extracting hire agreement data using Claude Vision:", file.name);
    if (isMockMode) {
      console.log("⚠️  Running in MOCK MODE - using mock extraction");
      return generateMockHireExtraction(file);
    }

    try {
      // Convert file to base64
      const base64Data = await fileToBase64(file);
      const mediaType = getMediaType(file);

      console.log('📤 Sending to Claude edge function...', {
        fileSize: file.size,
        mediaType,
        base64Length: base64Data.length
      });

      const extractedData = await withRetry(() =>
        invokeAiOrchestrator<any>('extract_hire_agreement_data', {
          mediaType,
          base64Data,
          fileName: file.name,
          fileSize: file.size
        })
      );
      const hireData = extractedData.data || {};

      console.log('✅ Hire agreement extraction successful:', hireData);

      const lowConfidenceFields: string[] = Array.isArray(hireData?.low_confidence_fields)
        ? hireData.low_confidence_fields
        : [];

      return {
        vrm: hireData.vrm || 'UNKNOWN',
        vehicle_owner_name: hireData.vehicle_owner_name || null,
        vehicle_owner_email: hireData.vehicle_owner_email || null,
        vehicle_owner_phone: hireData.vehicle_owner_phone || null,
        vehicle_owner_address: hireData.vehicle_owner_address || null,
        owner_authorization_present: Boolean(hireData.owner_authorization_present),
        driver_name: hireData.driver_name || 'Unknown Driver',
        driver_email: hireData.driver_email || null,
        driver_phone: hireData.driver_phone || null,
        hire_ref: hireData.hire_ref || null,
        start_date: hireData.start_date || new Date().toISOString().split('T')[0],
        end_date: hireData.end_date || null,
        company_name: hireData.company_name || null,
        has_liability_clause: hireData.has_liability_clause || false,
        has_signature: hireData.has_signature || false,
        low_confidence_fields: lowConfidenceFields
      };

    } catch (error: any) {
      console.error('❌ AI hire agreement extraction failed:', error);
      console.error('Error details:', {
        message: error?.message,
        name: error?.name,
        stack: error?.stack,
        fullError: error
      });

      // Check if it's an auth error
      if (error?.message?.includes('session') || error?.message?.includes('Unauthorized')) {
        console.error('🔒 Authentication error - user may need to log in again');
        throw new Error('Authentication failed. Please log out and log in again.');
      }

      console.warn('⚠️  Falling back to mock extraction');

      // Graceful fallback to mock data
      return generateMockHireExtraction(file);
    }
  },

  // 6. Predictive Analytics - Driver Risk Scoring
  async predictDriverRisk(driverName: string, recentTickets: Ticket[]): Promise<{
    riskScore: number;
    riskLevel: 'Low' | 'Medium' | 'High' | 'Critical';
    insights: string[];
    recommendations: string[];
  }> {
    console.log("🤖 Analyzing driver risk for:", driverName);
    if (isMockMode) return generateMockRiskPrediction(driverName, recentTickets);

    try {
      // Build context from tickets
      const ticketSummary = recentTickets.map(t =>
        `${new Date(t.contravention_date).toLocaleDateString()}: ${t.contravention_type} at ${t.location} (£${t.amount})`
      ).join('\n');

      const prompt = `Analyze the following driver's recent parking/traffic violation history and predict their risk level.

Driver: ${driverName}
Recent Violations (last 90 days):
${ticketSummary || 'No recent violations'}

Provide:
1. Risk score (0-100, where 100 is highest risk)
2. Risk level (Low/Medium/High/Critical)
3. Key insights (3-5 bullet points about patterns)
4. Actionable recommendations (2-3 specific actions)

Consider: frequency, violation types, cost trends, location patterns.`;

      const response = await withRetry(() =>
        invokeAiOrchestrator<any>('analyze_risk', {
          prompt,
          driverName,
          ticketCount: recentTickets.length
        })
      );
      const risk = response.data || {};

      return {
        riskScore: risk.riskScore || calculateBasicRiskScore(recentTickets),
        riskLevel: risk.riskLevel || determineRiskLevel(recentTickets.length),
        insights: risk.insights || [],
        recommendations: risk.recommendations || []
      };

    } catch (error: any) {
      console.error('❌ Risk prediction failed:', error);
      return generateMockRiskPrediction(driverName, recentTickets);
    }
  },

  // 6. Smart Appeal Strategy Advisor (ENHANCED WITH COMPREHENSIVE FACTORS)
  async suggestAppealStrategy(ticket: Ticket): Promise<{
    recommendedStrategy: string;
    successProbability: number;
    reasoning: string;
    requiredEvidence: string[];
    legalGrounds: string[];
  }> {
    console.log("🤖 Analyzing appeal strategy for:", ticket.pcn_number);
    if (isMockMode) return generateMockAppealStrategy(ticket);

    try {
      // Calculate time-sensitive factors
      const now = new Date();
      const issueDate = new Date(ticket.issue_date);
      const contraventionDate = new Date(ticket.contravention_date);
      const daysSinceIssue = Math.floor((now.getTime() - issueDate.getTime()) / (1000 * 60 * 60 * 24));
      const daysSinceContravention = Math.floor((now.getTime() - contraventionDate.getTime()) / (1000 * 60 * 60 * 24));
      const serviceDelay = Math.floor((issueDate.getTime() - contraventionDate.getTime()) / (1000 * 60 * 60 * 24));
      const discountDaysRemaining = ticket.discount_amount ? (14 - daysSinceIssue) : 0;
      const appealDeadlineDaysRemaining = 28 - daysSinceIssue;

      // Determine enforcement type for legal framework
      const isCouncil = ticket.issuing_authority?.toLowerCase().includes('council') ||
                        ticket.issuing_authority?.toLowerCase().includes('tfl');
      const enforcementType = isCouncil ? 'Traffic Enforcement (TMA 2004)' : 'Private Parking (PoFA 2012)';
      const appealBody = isCouncil ? 'Traffic Penalty Tribunal (TPT)' : 'POPLA (Parking on Private Land Appeals)';

      // Build comprehensive prompt with ALL outcome-affecting variables
      const prompt = `As an expert UK parking law specialist with 15+ years experience analyzing PCN appeals, provide a comprehensive appeal strategy assessment.

=== TICKET INFORMATION ===
PCN Number: ${ticket.pcn_number}
Vehicle Registration: ${ticket.vrn}
Issuing Authority: ${ticket.issuing_authority}
Enforcement Type: ${enforcementType}
Appeal Body: ${appealBody}

=== LOCATION & CONTRAVENTION ===
Location: ${ticket.location}
Contravention Type: ${ticket.contravention_type}
Contravention Reason: ${ticket.contravention_reason || 'Not specified'}
Date of Contravention: ${contraventionDate.toLocaleDateString('en-GB')} (${daysSinceContravention} days ago)
Time of Contravention: ${ticket.contravention_time || 'Not specified'}

=== TIMELINE & PROCEDURAL FACTORS ===
Issue Date: ${issueDate.toLocaleDateString('en-GB')} (${daysSinceIssue} days ago)
Service Delay: ${serviceDelay} days (statutory limit: 28 days for postal PCN)
Current Stage: ${ticket.stage}
Days Until Appeal Deadline: ${appealDeadlineDaysRemaining} days
${ticket.discount_amount ? `Discount Period: ${discountDaysRemaining > 0 ? `${discountDaysRemaining} days remaining` : 'EXPIRED'}` : 'No discount offered'}

=== FINANCIAL FACTORS ===
Full Amount: £${ticket.amount}
${ticket.discount_amount ? `Discounted Amount: £${ticket.discount_amount} (if paid within 14 days)` : ''}
Amount at Risk if Appeal Fails: £${ticket.amount}
Potential Savings if Successful: £${ticket.amount}

=== EVIDENCE & DOCUMENTATION ===
Evidence Photos Available: ${ticket.evidence_url ? 'Yes' : 'No'}
Supporting Documents: ${ticket.notes ? 'Driver provided notes/context' : 'None'}
Driver Statement: ${ticket.assigned_driver_name ? `Assigned to ${ticket.assigned_driver_name}` : 'No driver assigned'}
Low Confidence Fields (from AI extraction): ${ticket.low_confidence_fields?.join(', ') || 'None'}

=== CRITICAL ANALYSIS FACTORS ===
Please analyze the following factors that statistically affect appeal outcomes:

1. PROCEDURAL GROUNDS (Highest Success Rate: 70-85%)
   - Service delay: ${serviceDelay} days vs 28-day limit
   - Was PCN attached to vehicle or posted? ${ticket.stage?.toLowerCase().includes('notice to owner') ? 'Postal (NtO stage)' : 'Likely attached'}
   - Notice to Owner received within statutory timeframe?
   - Charge Certificate procedure followed correctly?

2. TECHNICAL/EVIDENTIAL GROUNDS (Success Rate: 40-60%)
   - Signage quality and visibility at ${ticket.location}
   - Are restriction signs compliant with Traffic Signs Regulations 2016?
   - Clear evidence of contravention vs ambiguous circumstances?
   - VRM correctly recorded (check for OCR errors)?
   - Location description accurate and specific?

3. LOADING/UNLOADING EXEMPTION (Success Rate: 35-50%)
   - Contravention type: ${ticket.contravention_type}
   - Was vehicle engaged in active loading/unloading?
   - Duration and necessity of stop
   - Goods/passengers being loaded?

4. MITIGATING CIRCUMSTANCES (Success Rate: 25-40%)
   - Medical emergency
   - Vehicle breakdown
   - Unclear or missing signage
   - Driver unfamiliarity with area
   - Disputed liability (hire/lease situations)

5. AUTHORITY TRACK RECORD
   - ${ticket.issuing_authority} appeal acceptance rate (if known)
   - Known for strict enforcement or reasonable approach?
   - Specific location ${ticket.location} - is this a known "trap" location?

6. STAGE-SPECIFIC FACTORS
   - Current stage: ${ticket.stage}
   - Available remedies at this stage
   - Escalation risk if appeal fails
   - Alternative options (Tribunal, statutory declaration, etc.)

=== REQUIRED OUTPUT ===
Provide:
1. **Recommended Strategy**: Choose the SINGLE best strategy from:
   - "Procedural Error" (late service, incorrect procedure)
   - "Signage Non-Compliance" (inadequate/missing signs)
   - "Loading/Unloading Exemption" (active loading)
   - "Technical Error" (wrong VRM, location, dates)
   - "Mitigating Circumstances" (emergency, breakdown)
   - "No Valid Grounds" (pay or accept)

2. **Success Probability**: 0-100% based on strength of grounds and historical data

3. **Detailed Reasoning**: 3-4 sentences explaining:
   - Why this strategy has the best chance
   - Specific strengths in this case
   - Key weaknesses to address
   - Comparison to alternative strategies

4. **Required Evidence**: Specific list of documents/photos needed:
   - Photos (specify what to photograph and from what angles)
   - Statements (from whom and what they should contain)
   - Documents (hire agreements, delivery notes, medical certificates)
   - Timeline evidence (CCTV requests, witness statements)

5. **Legal Grounds**: Cite specific UK regulations/case law:
   - Traffic Management Act 2004 sections
   - Civil Enforcement of Parking Contraventions Regulations 2007
   - Traffic Signs Regulations and General Directions 2016
   - Protection of Freedoms Act 2012 (for private parking)
   - Relevant case law (if applicable)

Consider the cost-benefit: Is fighting worth £${ticket.amount} vs ${ticket.discount_amount || Math.round(ticket.amount * 0.5)} discounted? Factor in time, stress, and success probability.`;

      const response = await withRetry(() =>
        invokeAiOrchestrator<any>('suggest_appeal', { prompt, ticket })
      );
      const strategy = response.data || {};

      return {
        recommendedStrategy: strategy.recommendedStrategy || 'Mitigating Circumstances',
        successProbability: strategy.successProbability || 50,
        reasoning: strategy.reasoning || 'Analysis unavailable',
        requiredEvidence: strategy.requiredEvidence || [],
        legalGrounds: strategy.legalGrounds || []
      };

    } catch (error: any) {
      console.error('❌ Appeal strategy suggestion failed:', error);
      return generateMockAppealStrategy(ticket);
    }
  },

  // 7. Document Classification
  async classifyDocument(file: File): Promise<{
    documentType: 'PCN' | 'NtO' | 'Charge Certificate' | 'Court Summons' | 'Other';
    confidence: number;
    stage: string;
    urgency: 'Low' | 'Medium' | 'High' | 'Critical';
    daysUntilDeadline: number | null;
  }> {
    console.log("🤖 Classifying document:", file.name);
    if (isMockMode) return generateMockClassification(file);

    try {
      const base64Data = await fileToBase64(file);
      const mediaType = getMediaType(file);

      const response = await withRetry(() =>
        invokeAiOrchestrator<any>('classify_document', {
          mediaType,
          base64Data,
          filename: file.name,
          fileName: file.name,
          fileSize: file.size
        })
      );
      const classification = response.data || {};

      return {
        documentType: classification.documentType || 'PCN',
        confidence: classification.confidence || 0.5,
        stage: classification.stage || 'Unknown',
        urgency: classification.urgency || 'Medium',
        daysUntilDeadline: classification.daysUntilDeadline || null
      };

    } catch (error: any) {
      console.error('❌ Document classification failed:', error);
      return generateMockClassification(file);
    }
  },

  // 8. Council Communication Assistant
  async draftCouncilEmail(ticket: Ticket, purpose: string, context: string): Promise<string> {
    console.log("🤖 Drafting council email for:", purpose);
    if (isMockMode) return generateMockCouncilEmail(ticket, purpose);

    try {
      const prompt = `Draft a professional email to ${ticket.issuing_authority} regarding PCN ${ticket.pcn_number}.

Purpose: ${purpose}
Context: ${context}

Ticket Details:
- Vehicle: ${ticket.vrn}
- Location: ${ticket.location}
- Date: ${new Date(ticket.contravention_date).toLocaleDateString()}
- Amount: £${ticket.amount}

Write a formal, polite email (150-250 words) with:
- Professional greeting
- Clear statement of purpose
- Relevant details
- Specific request
- Professional closing

Sign as "Fleet Manager".`;

      const email = await withRetry(() =>
        invokeAiOrchestrator<string>('draft_email', { prompt, ticket, purpose })
      );

      return email.data;

    } catch (error: any) {
      console.error('❌ Email drafting failed:', error);
      return generateMockCouncilEmail(ticket, purpose);
    }
  }
};

/**
 * Generate mock extraction data as fallback when AI is unavailable
 */
function generateMockExtraction(file: File) {
  console.log("⚠️  Using mock extraction for:", file.name);

  const today = new Date();
  const twoDaysAgo = new Date(today.getTime() - (2 * 24 * 60 * 60 * 1000));

  // Randomize low confidence fields for demonstration
  const lowConfidence: string[] = [];
  if (Math.random() > 0.7) lowConfidence.push('vrn');
  if (Math.random() > 0.8) lowConfidence.push('amount');

  // Randomize classification
  const types = ['Parking Violation', 'Bus Lane', 'Moving Traffic', 'Congestion Charge'];
  const type = types[Math.floor(Math.random() * types.length)];

  // Randomize Location
  const locations = ['High Street', 'Station Road', 'London Road', 'Church Street', 'Main Street', 'Victoria Road', 'Park Avenue', 'Market Place', 'Bridge Street', 'King Street'];
  const location = locations[Math.floor(Math.random() * locations.length)];

  // Pricing logic
  const fullAmount = 130.00;
  const discountAmount = 65.00;

  return {
    pcn_number: "MOCK-" + Math.floor(Math.random() * 100000),
    vrn: "AB12 CDE",
    issue_date: today.toISOString().split('T')[0] + "T12:00:00",
    contravention_date: twoDaysAgo.toISOString().split('T')[0] + "T09:30:00",
    amount: fullAmount,
    discount_amount: discountAmount,
    location: location,
    issuing_authority: "Westminster City Council",
    stage: 'Notice to Owner',
    contravention_type: type,
    low_confidence_fields: lowConfidence
  };
}

/**
 * Generate template-based appeal letter as fallback when AI is unavailable
 */
function generateTemplateAppeal(ticket: Ticket, reason: string): string {
  console.log("⚠️  Using template appeal for reason:", reason);

  const templates: Record<string, string> = {
    'Loading': `Dear Sir/Madam,

I am writing to formally challenge PCN ${ticket.pcn_number} issued on ${new Date(ticket.issue_date).toLocaleDateString()} regarding vehicle ${ticket.vrn}.

The alleged contravention occurred at ${ticket.location}. However, I contend that the contravention did not occur. The vehicle was engaged in active loading/unloading operations at the time, which is a permitted activity at this location.

Please find attached the delivery manifest and a signed statement from the driver confirming the activity. Based on this evidence, I request that this PCN be cancelled immediately.

Yours faithfully,
Fleet Manager`,

    'Signage': `Dear Sir/Madam,

Re: PCN ${ticket.pcn_number} | Vehicle: ${ticket.vrn}

I wish to appeal this penalty charge issued at ${ticket.location}. Upon reviewing the driver's evidence, it is clear that the signage indicating the restriction was either missing, obscured, or not compliant with current regulations.

Under the Traffic Signs Regulations and General Directions 2016, adequate notice must be given. The driver could not reasonably be expected to know of the restriction.

I trust you will cancel this charge.

Sincerely,
Fleet Manager`,

    'Stolen': `To ${ticket.issuing_authority},

I am challenging PCN ${ticket.pcn_number} on the grounds that the vehicle ${ticket.vrn} had been taken without the owner's consent at the time of the contravention.

Please find attached the Crime Reference Number provided by the police. We have already updated the DVLA regarding this incident.

Please confirm cancellation of this ticket.

Regards,
Fleet Admin`,

    'Hire': `To Whom It May Concern,

Reference: ${ticket.pcn_number}

We are a vehicle hire company. At the time of the contravention (${new Date(ticket.contravention_date).toLocaleString()}), the vehicle ${ticket.vrn} was on hire to a third party.

In accordance with the Road Traffic Offenders Act, we are transferring liability to the hirer. Please find enclosed the signed hire agreement containing the driver's details and their statement of liability.

Please reissue the Notice to Owner to the hirer directly.

Sincerely,
Fleet Team`,

    'Procedural': `Dear Sir/Madam,

Challenge regarding PCN ${ticket.pcn_number}

I am writing to make representations against this penalty charge on the statutory ground of "procedural impropriety".

Specifically, the Penalty Charge Notice appears to have been served outside of the statutory time limit defined in the Civil Enforcement of Parking Contraventions (England) General Regulations 2007. The alleged contravention occurred on ${new Date(ticket.contravention_date).toLocaleDateString()}, yet the issue date of this notice is ${new Date(ticket.issue_date).toLocaleDateString()}.

As the regulations generally state that a PCN must be served within 28 days of the contravention, this notice is time-barred and invalid. I request that it be cancelled immediately.

Yours faithfully,
Fleet Manager`
  };

  const defaultTemplate = `Dear Sir/Madam,

I am writing to appeal PCN ${ticket.pcn_number} for vehicle ${ticket.vrn}. The contravention did not occur due to mitigating circumstances: ${reason}.

Please review the evidence provided and cancel this charge.

Sincerely,
Fleet Manager`;

  return templates[reason] || defaultTemplate;
}

/**
 * Fetch fleet context data for RAG approach
 * Queries real database for relevant fleet metrics
 */
async function fetchFleetContext(): Promise<string> {
  try {
    // Dynamically import ApiService to avoid circular dependency
    const { ApiService } = await import('./api');

    // Fetch data in parallel for better performance
    const [ticketsResult, drivers, vehicles, tasks] = await Promise.all([
      ApiService.getTickets({ limit: 1000 }), // Get last 1000 tickets for analysis
      ApiService.getDrivers(),
      ApiService.getVehicles(),
      ApiService.getTasks()
    ]);

    const tickets: Ticket[] = (ticketsResult.data || []) as Ticket[];
    const totalTickets = ticketsResult.count || 0;
    const typedTasks: Array<{ status?: string }> = (tasks || []) as Array<{ status?: string }>;

    // Calculate active tickets (not paid or cancelled)
    const activeTickets = tickets.filter(t =>
      t.status !== 'paid' && t.status !== 'cancelled'
    );
    const activeCount = activeTickets.length;

    // Calculate pending appeals
    const pendingAppeals = tickets.filter(t =>
      t.status === 'appeal_pending' || t.decision === 'appeal'
    ).length;

    // Calculate total monthly cost (last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const recentTickets = tickets.filter(t =>
      new Date(t.contravention_date) >= thirtyDaysAgo
    );
    const monthlyCost = recentTickets.reduce((sum, t) => sum + (t.amount || 0), 0);

    // Find top issuing authority
    const authorityCount: Record<string, number> = {};
    tickets.forEach(t => {
      const auth = t.issuing_authority || 'Unknown';
      authorityCount[auth] = (authorityCount[auth] || 0) + 1;
    });
    const topAuthority = Object.entries(authorityCount)
      .sort((a, b) => b[1] - a[1])[0];
    const topAuthorityName = topAuthority ? topAuthority[0] : 'None';
    const topAuthorityPercent = topAuthority && totalTickets > 0
      ? Math.round((topAuthority[1] / totalTickets) * 100)
      : 0;

    // Find highest risk driver (most tickets in last 30 days)
    const driverTicketCount: Record<string, number> = {};
    recentTickets.forEach(t => {
      if (t.assigned_driver_name) {
        driverTicketCount[t.assigned_driver_name] = (driverTicketCount[t.assigned_driver_name] || 0) + 1;
      }
    });
    const highestRiskDriver = Object.entries(driverTicketCount)
      .sort((a, b) => b[1] - a[1])[0];
    const riskDriverName = highestRiskDriver ? highestRiskDriver[0] : 'None';
    const riskDriverCount = highestRiskDriver ? highestRiskDriver[1] : 0;
    const riskLevel = riskDriverCount >= 4 ? 'High' : riskDriverCount >= 2 ? 'Medium' : 'Low';

    // Calculate most common violation type
    const violationCount: Record<string, number> = {};
    tickets.forEach(t => {
      const type = t.contravention_type || 'Unknown';
      violationCount[type] = (violationCount[type] || 0) + 1;
    });
    const sortedViolations = Object.entries(violationCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);
    const violationBreakdown = sortedViolations.map(([type, count]) =>
      `${type} (${totalTickets > 0 ? Math.round((count / totalTickets) * 100) : 0}%)`
    ).join(', ');

    // Find peak violation day and time
    const dayCount: Record<string, number> = {};
    recentTickets.forEach(t => {
      const date = new Date(t.contravention_date);
      const day = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][date.getDay()];
      dayCount[day] = (dayCount[day] || 0) + 1;
    });
    const peakDay = Object.entries(dayCount)
      .sort((a, b) => b[1] - a[1])[0];
    const peakDayName = peakDay ? peakDay[0] : 'N/A';

    // Calculate average fine amount
    const avgFine = totalTickets > 0
      ? Math.round(tickets.reduce((sum, t) => sum + (t.amount || 0), 0) / totalTickets)
      : 0;

    // Find vehicles with most tickets
    const vehicleTicketCount: Record<string, number> = {};
    tickets.forEach(t => {
      if (t.vrn) {
        vehicleTicketCount[t.vrn] = (vehicleTicketCount[t.vrn] || 0) + 1;
      }
    });
    const topVehicles = Object.entries(vehicleTicketCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 2)
      .map(([vrn, count]) => `${vrn} (${count})`)
      .join(', ') || 'None';

    // Count pending tasks
    const pendingTasks = typedTasks.filter((t) => t.status === 'pending').length;

    // Calculate advanced analytics
    const transferableTickets = tickets.filter(t =>
      (t.stage?.toLowerCase().includes('pcn') || t.stage?.toLowerCase().includes('notice to owner')) &&
      t.status !== 'paid' && t.status !== 'cancelled'
    ).length;

    const lateStageTickets = tickets.filter(t =>
      t.stage?.toLowerCase().includes('charge certificate') || t.stage?.toLowerCase().includes('order for recovery')
    ).length;

    const potentialSavings = activeTickets
      .filter(t => t.discount_amount)
      .reduce((sum, t) => sum + ((t.amount || 0) - (t.discount_amount || 0)), 0);

    const avgDaysToResolve = tickets.filter(t => t.status === 'paid' || t.status === 'cancelled').length > 0
      ? Math.round(tickets.filter(t => t.status === 'paid' || t.status === 'cancelled')
        .reduce((sum, t) => {
          const issue = new Date(t.issue_date).getTime();
          const resolved = new Date(t.updated_at || t.created_at || t.issue_date).getTime();
          return sum + Math.floor((resolved - issue) / (1000 * 60 * 60 * 24));
        }, 0) / tickets.filter(t => t.status === 'paid' || t.status === 'cancelled').length)
      : 0;

    const escalationRate = totalTickets > 0
      ? Math.round((lateStageTickets / totalTickets) * 100)
      : 0;

    // Build enhanced context string with analytical insights
    return `
=== FLEET PERFORMANCE METRICS (Last 30 Days) ===
Total Tickets: ${totalTickets} (${activeCount} active, ${totalTickets - activeCount} resolved)
Monthly Cost: £${monthlyCost.toLocaleString()} (${recentTickets.length} tickets)
Pending Appeals: ${pendingAppeals}
Transferable Tickets: ${transferableTickets} (PCN/NtO stage - action required)
Late-Stage Tickets: ${lateStageTickets} (CC/OFR - urgent)
Escalation Rate: ${escalationRate}% (tickets reaching CC/OFR stage)

=== COST OPTIMIZATION OPPORTUNITIES ===
Potential Discount Savings: £${potentialSavings.toLocaleString()} (if paid within 14 days)
Average Fine Amount: £${avgFine}
Average Resolution Time: ${avgDaysToResolve} days

=== RISK ANALYSIS ===
Highest Risk Driver: ${riskDriverName} (${riskDriverCount} tickets in 30 days)
Risk Level: ${riskLevel}
Vehicles with Most Tickets: ${topVehicles}
Peak Violation Day: ${peakDayName}

=== ENFORCEMENT PATTERNS ===
Top Issuing Authority: ${topAuthorityName} (${topAuthorityPercent}% of all tickets)
Most Common Violations: ${violationBreakdown || 'None'}

=== FLEET RESOURCES ===
Total Vehicles: ${vehicles.length}
Total Drivers: ${drivers.length}
Pending Tasks: ${pendingTasks}

=== ACTIONABLE INSIGHTS ===
- ${transferableTickets > 0 ? `⚠️ ${transferableTickets} tickets can still be transferred to hirers - DO NOT PAY, TRANSFER!` : '✓ No pending transfers'}
- ${lateStageTickets > 0 ? `🚨 ${lateStageTickets} tickets at late stage (CC/OFR) - URGENT ACTION REQUIRED` : '✓ No late-stage tickets'}
- ${potentialSavings > 0 ? `💰 £${potentialSavings.toLocaleString()} can be saved by paying active tickets within discount period` : ''}
- ${escalationRate > 15 ? `⚠️ High escalation rate (${escalationRate}%) - review internal processes` : ''}
- ${riskDriverCount >= 3 ? `⚠️ Driver ${riskDriverName} needs immediate training/review` : ''}
    `.trim();

  } catch (error) {
    console.error('❌ Failed to fetch fleet context:', error);
    // Fallback to basic context on error
    return `
Fleet data unavailable. Please ensure tickets, drivers, and vehicles are loaded.
Ask me about specific PCN numbers, drivers, or general fleet management advice.
    `.trim();
  }
}

/**
 * Generate keyword-based response as fallback when AI is unavailable
 */
function generateKeywordResponse(message: string): string {
  console.log("⚠️  Using keyword matching for:", message);

  const msg = message.toLowerCase();

  if (msg.includes('risk') || msg.includes('driver')) {
    return "Based on recent data, **John Smith** has the highest risk score (High) with 4 PCNs in the last 30 days. I recommend scheduling a training review.";
  }
  if (msg.includes('westminster') || msg.includes('council')) {
    return "Westminster City Council is your top issuer this month, accounting for **35% of all fines**. Most occur on Tuesdays between 9 AM and 11 AM.";
  }
  if (msg.includes('cost') || msg.includes('spend')) {
    return "Your estimated liability for this month is **£1,200**. This is a 15% decrease from last month. Good job!";
  }

  return "I can help you analyze fleet performance, draft appeals, or identify high-risk drivers. Try asking: 'Who is my highest risk driver?'";
}

/**
 * Generate mock compliance result as fallback when AI is unavailable
 */
function generateMockComplianceResult(file: File, options?: { expectedHirerName?: string; expectedVRM?: string }): ComplianceResult {
  console.log("⚠️  Using mock compliance scoring for:", file.name);

  // Randomly determine result quality for demo purposes
  const quality = Math.random();

  const mockVRM = options?.expectedVRM || 'AB12 CDE';
  const mockHirerName = options?.expectedHirerName || 'John Smith';

  if (quality > 0.6) {
    // Good Agreement
    return {
      score: 95,
      status: 'Compliant',
      summary: "This agreement appears to be fully compliant. We detected the driver's signature and a specific clause transferring liability for PCNs.",
      missing_clauses: [],
      recommendations: ["Ensure the driver's signature is legible on all copies."],
      detected_fields: { vrm: true, dates: true, signature: true, liability_statement: true },
      extracted_data: {
        vrm: mockVRM,
        hirer_name: mockHirerName,
        liability_clause_text: "The hirer acknowledges full responsibility and liability for all Penalty Charge Notices (PCNs), parking charges, fines, fees, costs, and any associated expenses incurred during the hire period.",
        hire_start: new Date().toISOString().split('T')[0],
        hire_end: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      }
    };
  } else if (quality > 0.3) {
    // Minor Issues
    return {
      score: 75,
      status: 'At Risk',
      summary: "The agreement is valid but lacks a specific reference to the Traffic Management Act 2004 in the liability clause, which some councils may challenge.",
      missing_clauses: ["Explicit reference to TMA 2004", "Start/End time (HH:MM)"],
      recommendations: [
        "Update your liability clause to specifically cite the 'Traffic Management Act 2004'.",
        "Include exact pickup and return times to avoid disputes for contraventions on handover days."
      ],
      detected_fields: { vrm: true, dates: true, signature: true, liability_statement: true },
      extracted_data: {
        vrm: mockVRM,
        hirer_name: mockHirerName,
        liability_clause_text: "The hirer accepts responsibility for parking fines during the rental period.",
        hire_start: new Date().toISOString().split('T')[0],
        hire_end: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      }
    };
  } else {
    // Bad Agreement
    return {
      score: 45,
      status: 'Non-Compliant',
      summary: "This document is at high risk of rejection. We could not find a clear statement where the hirer accepts liability for Penalty Charge Notices.",
      missing_clauses: ["Liability Clause (PCNs)", "Driver Signature", "Vehicle Make/Model"],
      recommendations: [
        "IMMEDIATE ACTION: Add a clause stating: 'The hirer accepts liability for all Fixed Penalty Notices and PCNs issued during the hire period.'",
        "Ensure the vehicle is clearly identified by VRM.",
        "Obtain a wet or digital signature from the hirer."
      ],
      detected_fields: { vrm: true, dates: true, signature: false, liability_statement: false },
      extracted_data: {
        vrm: mockVRM,
        hirer_name: mockHirerName
      }
    };
  }
}

/**
 * Helper: Calculate basic risk score from ticket count
 */
function calculateBasicRiskScore(tickets: Ticket[]): number {
  const count = tickets.length;
  if (count === 0) return 0;
  if (count === 1) return 20;
  if (count === 2) return 40;
  if (count === 3) return 60;
  if (count >= 4) return 80;
  return Math.min(100, count * 15);
}

/**
 * Helper: Determine risk level from ticket count
 */
function determineRiskLevel(count: number): 'Low' | 'Medium' | 'High' | 'Critical' {
  if (count === 0) return 'Low';
  if (count <= 2) return 'Medium';
  if (count <= 4) return 'High';
  return 'Critical';
}

/**
 * Generate mock risk prediction as fallback
 */
function generateMockRiskPrediction(driverName: string, tickets: Ticket[]) {
  const count = tickets.length;
  const riskScore = calculateBasicRiskScore(tickets);
  const riskLevel = determineRiskLevel(count);

  const insights = [
    `Driver: ${driverName}`,
    `Driver has ${count} violation${count !== 1 ? 's' : ''} in the last 90 days`,
    count > 2 ? 'Pattern suggests need for driver training' : 'Violation frequency is within normal range',
    'Most violations occur during weekday commute hours'
  ];

  const recommendations = count > 2
    ? ['Schedule driver training session', 'Review parking policies with driver', 'Monitor for next 30 days']
    : ['Continue monitoring', 'No immediate action required'];

  return { riskScore, riskLevel, insights, recommendations };
}

/**
 * Generate mock appeal strategy as fallback
 */
function generateMockAppealStrategy(ticket: Ticket) {
  // Analyze ticket details to suggest strategy
  const isOld = (new Date().getTime() - new Date(ticket.issue_date).getTime()) > (28 * 24 * 60 * 60 * 1000);

  if (isOld) {
    return {
      recommendedStrategy: 'Procedural',
      successProbability: 75,
      reasoning: 'PCN was issued more than 28 days after the contravention, potentially violating statutory time limits.',
      requiredEvidence: ['Proof of contravention date', 'Proof of PCN receipt date'],
      legalGrounds: ['Civil Enforcement of Parking Contraventions (England) General Regulations 2007']
    };
  }

  if (ticket.contravention_type === 'Parking') {
    return {
      recommendedStrategy: 'Signage',
      successProbability: 60,
      reasoning: 'Parking violations often succeed on grounds of inadequate signage or unclear restrictions.',
      requiredEvidence: ['Photos of signage at location', 'Photos showing vehicle position', 'Street view evidence'],
      legalGrounds: ['Traffic Signs Regulations and General Directions 2016', 'Local authority duty to provide clear signage']
    };
  }

  return {
    recommendedStrategy: 'Mitigating Circumstances',
    successProbability: 50,
    reasoning: 'General appeal based on circumstances of the case.',
    requiredEvidence: ['Statement from driver', 'Any supporting documentation'],
    legalGrounds: ['General grounds for representation under TMA 2004']
  };
}

/**
 * Generate mock document classification as fallback
 */
function generateMockClassification(file: File) {
  const filename = file.name.toLowerCase();

  // Simple heuristic based on filename
  if (filename.includes('pcn') || filename.includes('penalty')) {
    return {
      documentType: 'PCN' as const,
      confidence: 0.8,
      stage: 'PCN Issued',
      urgency: 'Medium' as const,
      daysUntilDeadline: 28
    };
  }

  if (filename.includes('nto') || filename.includes('notice')) {
    return {
      documentType: 'NtO' as const,
      confidence: 0.7,
      stage: 'Notice to Owner',
      urgency: 'High' as const,
      daysUntilDeadline: 28
    };
  }

  if (filename.includes('charge') || filename.includes('certificate')) {
    return {
      documentType: 'Charge Certificate' as const,
      confidence: 0.75,
      stage: 'Charge Certificate',
      urgency: 'Critical' as const,
      daysUntilDeadline: 14
    };
  }

  return {
    documentType: 'PCN' as const,
    confidence: 0.5,
    stage: 'Unknown',
    urgency: 'Medium' as const,
    daysUntilDeadline: null
  };
}

/**
 * Generate mock council email as fallback
 */
function generateMockCouncilEmail(ticket: Ticket, purpose: string): string {
  return `Dear ${ticket.issuing_authority},

Re: Penalty Charge Notice ${ticket.pcn_number} - Vehicle Registration ${ticket.vrn}

I am writing regarding the above PCN issued on ${new Date(ticket.issue_date).toLocaleDateString()} for an alleged contravention at ${ticket.location}.

${purpose === 'Request Evidence' ?
  'I wish to formally request copies of all photographic and documentary evidence relating to this PCN, as is my right under the Freedom of Information Act 2000.' :
  purpose === 'Appeal' ?
  'I wish to make formal representations against this PCN. I believe the contravention did not occur for the reasons outlined in the attached appeal letter.' :
  'I am writing to request clarification regarding this matter.'}

I look forward to your response within the statutory timeframe.

Yours faithfully,
Fleet Manager`;
}

/**
 * Generate mock hire agreement extraction data as fallback
 */
function generateMockHireExtraction(file: File) {
  console.log("⚠️  Using mock hire agreement extraction for:", file.name);

  const today = new Date();
  const startDate = new Date(today.getTime() - (7 * 24 * 60 * 60 * 1000)); // 7 days ago
  const endDate = new Date(today.getTime() + (23 * 24 * 60 * 60 * 1000)); // 23 days from now

  // Randomize low confidence fields for demonstration
  const lowConfidence: string[] = [];
  if (Math.random() > 0.7) lowConfidence.push('driver_email');
  if (Math.random() > 0.8) lowConfidence.push('driver_phone');
  if (Math.random() > 0.9) lowConfidence.push('hire_ref');

  // Mock data
  const mockVRMs = ['AB12CDE', 'XY34FGH', 'LM56NOP', 'QR78STU', 'WX90VYZ'];
  const mockNames = ['John Smith', 'Sarah Johnson', 'Michael Brown', 'Emma Wilson', 'David Taylor'];
  const mockCompanies = ['ABC Rentals Ltd', 'FleetHire Solutions', 'Quick Car Hire', 'Premium Vehicle Rental', 'City Fleet Services'];

  const vrm = mockVRMs[Math.floor(Math.random() * mockVRMs.length)];
  const driverName = mockNames[Math.floor(Math.random() * mockNames.length)];
  const companyName = mockCompanies[Math.floor(Math.random() * mockCompanies.length)];

  return {
    vrm,
    vehicle_owner_name: companyName,
    vehicle_owner_email: Math.random() > 0.5 ? `fleet@${companyName.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '')}.com` : null,
    vehicle_owner_phone: Math.random() > 0.5 ? '0207' + Math.floor(Math.random() * 1000000).toString().padStart(6, '0') : null,
    vehicle_owner_address: Math.random() > 0.5 ? '1 Fleet Street, London, EC4Y 1AA' : null,
    owner_authorization_present: Math.random() > 0.4,
    driver_name: driverName,
    driver_email: Math.random() > 0.5 ? `${driverName.toLowerCase().replace(' ', '.')}@example.com` : null,
    driver_phone: Math.random() > 0.5 ? '07700 900' + Math.floor(Math.random() * 1000).toString().padStart(3, '0') : null,
    hire_ref: Math.random() > 0.3 ? `HIRE-${today.getFullYear()}-${Math.floor(Math.random() * 9000) + 1000}` : null,
    start_date: startDate.toISOString().split('T')[0],
    end_date: endDate.toISOString().split('T')[0],
    company_name: companyName,
    has_liability_clause: Math.random() > 0.3, // 70% have liability clause
    has_signature: Math.random() > 0.2, // 80% have signature
    low_confidence_fields: lowConfidence
  };
}
